/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：报告视觉主题工具，定义热点、归因、策略等报告入口的色彩、标签和卡片视觉变量。
 * - English: Report visual-theme utilities defining colors, labels, and card variables for hotspot, attribution, strategy, and related report entries.
 *
 * 调用关系 / Call Flow
 * - 中文：由报告阅读页和策略中心复用。
 * - English: Reused by report-reader pages and the strategy center.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

export const REPORT_VISUAL_THEMES = {
  default: {
    accent: '#2563eb',
    accentStrong: '#1e40af',
    headerBackground: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 54%, #eff6ff 100%)',
    headerBorder: '#bfdbfe',
    badgeBackground: '#dbeafe',
    badgeColor: '#1e40af',
    activeBackground: '#eff6ff',
    activeBorder: '#2563eb',
    activeShadow: '0 0 0 1px rgba(37, 99, 235, 0.14)',
    hoverBorder: '#93c5fd',
    selectedBackground: '#eff6ff',
    quoteBorder: '#2563eb',
    cardBackground: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    cardBorder: 'rgba(37, 99, 235, 0.10)',
    cardShadow: '0 18px 46px rgba(37, 99, 235, 0.07)',
    cardTagBackground: '#dbeafe',
    cardTagColor: '#1e40af',
    cardActionColor: '#1e40af',
    railBackground: 'linear-gradient(180deg, #0f172a 0%, #1e3a8a 58%, #2563eb 100%)',
    railEyebrow: '#bfdbfe',
    railShadow: '0 20px 48px rgba(37, 99, 235, 0.18)',
    railItemBackground: 'rgba(37, 99, 235, 0.08)',
    railItemBorder: 'rgba(96, 165, 250, 0.16)',
    railActionBorder: 'rgba(96, 165, 250, 0.34)',
    railActionBackground: 'rgba(37, 99, 235, 0.15)',
    railActionHoverBackground: 'rgba(37, 99, 235, 0.24)',
    railPillBackground: 'rgba(96, 165, 250, 0.13)',
    railPillBorder: 'rgba(147, 197, 253, 0.22)',
  },
  hotspot: {
    accent: '#ea580c',
    accentStrong: '#9a3412',
    headerBackground: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 50%, #fffbeb 100%)',
    headerBorder: '#fed7aa',
    badgeBackground: '#ffedd5',
    badgeColor: '#9a3412',
    activeBackground: '#fff7ed',
    activeBorder: '#ea580c',
    activeShadow: '0 0 0 1px rgba(234, 88, 12, 0.16)',
    hoverBorder: '#fdba74',
    selectedBackground: '#fff7ed',
    quoteBorder: '#ea580c',
    cardBackground: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 58%, #fffbeb 100%)',
    cardBorder: 'rgba(234, 88, 12, 0.16)',
    cardShadow: '0 18px 46px rgba(234, 88, 12, 0.09)',
    cardTagBackground: '#ffedd5',
    cardTagColor: '#9a3412',
    cardActionColor: '#9a3412',
    railBackground: 'linear-gradient(180deg, #1c1917 0%, #7c2d12 54%, #ea580c 100%)',
    railEyebrow: '#fed7aa',
    railShadow: '0 20px 48px rgba(234, 88, 12, 0.20)',
    railItemBackground: 'rgba(234, 88, 12, 0.11)',
    railItemBorder: 'rgba(253, 186, 116, 0.20)',
    railActionBorder: 'rgba(253, 186, 116, 0.38)',
    railActionBackground: 'rgba(234, 88, 12, 0.18)',
    railActionHoverBackground: 'rgba(234, 88, 12, 0.28)',
    railPillBackground: 'rgba(253, 186, 116, 0.13)',
    railPillBorder: 'rgba(254, 215, 170, 0.24)',
  },
  attribution: {
    accent: '#4f46e5',
    accentStrong: '#312e81',
    headerBackground: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 50%, #f5f3ff 100%)',
    headerBorder: '#c7d2fe',
    badgeBackground: '#e0e7ff',
    badgeColor: '#312e81',
    activeBackground: '#eef2ff',
    activeBorder: '#4f46e5',
    activeShadow: '0 0 0 1px rgba(79, 70, 229, 0.16)',
    hoverBorder: '#a5b4fc',
    selectedBackground: '#eef2ff',
    quoteBorder: '#4f46e5',
    cardBackground: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 58%, #f5f3ff 100%)',
    cardBorder: 'rgba(79, 70, 229, 0.16)',
    cardShadow: '0 18px 46px rgba(79, 70, 229, 0.09)',
    cardTagBackground: '#e0e7ff',
    cardTagColor: '#312e81',
    cardActionColor: '#312e81',
    railBackground: 'linear-gradient(180deg, #111827 0%, #312e81 54%, #4f46e5 100%)',
    railEyebrow: '#c7d2fe',
    railShadow: '0 20px 48px rgba(79, 70, 229, 0.20)',
    railItemBackground: 'rgba(79, 70, 229, 0.11)',
    railItemBorder: 'rgba(165, 180, 252, 0.20)',
    railActionBorder: 'rgba(165, 180, 252, 0.38)',
    railActionBackground: 'rgba(79, 70, 229, 0.18)',
    railActionHoverBackground: 'rgba(79, 70, 229, 0.28)',
    railPillBackground: 'rgba(165, 180, 252, 0.13)',
    railPillBorder: 'rgba(199, 210, 254, 0.24)',
  },
};

export const getReportVisualTheme = key =>
  REPORT_VISUAL_THEMES[key] || REPORT_VISUAL_THEMES.default;

export const buildReportReaderThemeStyle = theme => ({
  '--report-accent': theme.accent,
  '--report-accent-strong': theme.accentStrong,
  '--report-header-bg': theme.headerBackground,
  '--report-header-border': theme.headerBorder,
  '--report-badge-bg': theme.badgeBackground,
  '--report-badge-color': theme.badgeColor,
  '--report-active-bg': theme.activeBackground,
  '--report-active-border': theme.activeBorder,
  '--report-active-shadow': theme.activeShadow,
  '--report-hover-border': theme.hoverBorder,
  '--report-selected-bg': theme.selectedBackground,
  '--report-quote-border': theme.quoteBorder,
});

export const buildReportActionCardStyle = theme => ({
  '--report-card-bg': theme.cardBackground,
  '--report-card-border': theme.cardBorder,
  '--report-card-shadow': theme.cardShadow,
  '--report-card-tag-bg': theme.cardTagBackground,
  '--report-card-tag-color': theme.cardTagColor,
  '--report-card-action-color': theme.cardActionColor,
});
